import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";

export default function NurseAssignments({ admissionId }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const {
    data: assignments,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["ipd-nurse-assignments", admissionId],
    queryFn: () =>
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/ipd-admissions/${admissionId}/nurse-assignments/`,
          {
            headers: { Authorization: `Token ${token}` },
          }
        )
        .then((res) => {
          // Handle both list and single object responses
          if (Array.isArray(res.data)) {
            return res.data;
          }
          return res.data.results || [res.data].filter(Boolean);
        })
        .catch((err) => {
          // If 404, return empty array instead of throwing
          if (err.response?.status === 404) {
            return [];
          }
          throw err;
        }),
    enabled: !!admissionId && !!token,
    retry: false, // Don't retry on 404
  });

  const { data: nursesData } = useQuery({
    queryKey: ["nurses"],
    queryFn: () =>
      axios
        .get(`${process.env.REACT_APP_API_URL}/users/?page_size=1000`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => {
          // Filter to show nurses OR admins
          const allUsers = res.data?.results || res.data || [];
          const filtered = allUsers.filter(
            (user) => user.nurse === true || user.is_admin === true
          );
          return { ...res.data, results: filtered };
        }),
    enabled: !!token,
  });

  const nurses = nursesData?.results || nursesData || [];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Handle 404 gracefully - show empty state instead of error
  const assignmentsList = Array.isArray(assignments) ? assignments : (assignments?.results || []);
  
  if (isError && error?.response?.status !== 404) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || t("ipd.errors.loadFailed", "Failed to load nurse assignments.")}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          {t("ipd.nurseAssignments", "Nurse Assignments")}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAssignDialogOpen(true)}
        >
          {t("ipd.assignNurse", "Assign Nurse")}
        </Button>
      </Box>

      {assignmentsList.length === 0 && !isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("ipd.admissions.nurse_assignments.no_assignments", "No nurses assigned to this patient yet.")}
        </Alert>
      )}

      {assignmentsList.length > 0 ? (
        <List>
          {assignmentsList.map((assignment) => (
            <Card key={assignment.id} variant="outlined" sx={{ mb: 1 }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {assignment.nurse_name}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      <Chip
                        label={assignment.shift_display}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={assignment.role_display}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                      {assignment.is_active && (
                        <Chip
                          label={t("ipd.active", "Active")}
                          size="small"
                          color="success"
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      {t("ipd.assignedBy", "Assigned by")}: {assignment.assigned_by_name} •{" "}
                      {moment(assignment.start_time).format("YYYY-MM-DD HH:mm")}
                    </Typography>
                    {assignment.notes && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {assignment.notes}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </List>
      ) : (
        <Alert severity="info">
          {t("ipd.noNurseAssignments", "No nurses assigned to this patient.")}
        </Alert>
      )}

      <AssignNurseDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        admissionId={admissionId}
        nurses={nurses}
      />
    </Box>
  );
}

function AssignNurseDialog({ open, onClose, admissionId, nurses }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    nurse: "",
    shift: 1,
    role: 2,
    notes: "",
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open) {
      setFormData({
        nurse: "",
        shift: 1,
        role: 2,
        notes: "",
      });
      setErrors({});
    }
  }, [open]);

  const assignNurse = useMutation({
    mutationFn: (data) =>
      axios.post(
        `${process.env.REACT_APP_API_URL}/ipd-admissions/${admissionId}/assign-nurse/`,
        data,
        {
          headers: { Authorization: `Token ${token}` },
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["ipd-nurse-assignments", admissionId]);
      onClose();
    },
    onError: (error) => {
      console.error("Failed to assign nurse:", error);
      setErrors(error.response?.data || { non_field_errors: "Failed to assign nurse." });
    },
  });

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.nurse) {
      newErrors.nurse = t("ipd.errors.nurseRequired", "Nurse is required.");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    assignNurse.mutate(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("ipd.assignNurse", "Assign Nurse")}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {assignNurse.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.non_field_errors || assignNurse.error?.message || t("ipd.errors.genericError", "An error occurred.")}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense" error={!!errors.nurse}>
                <InputLabel id="nurse-label">{t("ipd.nurse", "Nurse")}</InputLabel>
                <Select
                  labelId="nurse-label"
                  id="nurse-select"
                  value={formData.nurse}
                  label={t("ipd.nurse", "Nurse")}
                  onChange={handleChange("nurse")}
                >
                  {nurses.map((nurse) => (
                    <MenuItem key={nurse.id} value={nurse.id}>
                      {nurse.first_name} {nurse.last_name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.nurse && <Typography variant="caption" color="error">{errors.nurse}</Typography>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="shift-label">{t("ipd.shift", "Shift")}</InputLabel>
                <Select
                  labelId="shift-label"
                  id="shift-select"
                  value={formData.shift}
                  label={t("ipd.shift", "Shift")}
                  onChange={handleChange("shift")}
                >
                  <MenuItem value={1}>{t("ipd.shiftMorning", "Morning")}</MenuItem>
                  <MenuItem value={2}>{t("ipd.shiftEvening", "Evening")}</MenuItem>
                  <MenuItem value={3}>{t("ipd.shiftNight", "Night")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="role-label">{t("ipd.role", "Role")}</InputLabel>
                <Select
                  labelId="role-label"
                  id="role-select"
                  value={formData.role}
                  label={t("ipd.role", "Role")}
                  onChange={handleChange("role")}
                >
                  <MenuItem value={1}>{t("ipd.rolePrimary", "Primary")}</MenuItem>
                  <MenuItem value={2}>{t("ipd.roleSecondary", "Secondary")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("ipd.notes", "Notes")}
                value={formData.notes}
                onChange={handleChange("notes")}
                error={!!errors.notes}
                helperText={errors.notes}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={assignNurse.isPending}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={assignNurse.isPending ? <CircularProgress size={20} /> : <AddIcon />}
            disabled={assignNurse.isPending}
          >
            {assignNurse.isPending ? t("common.saving", "Saving...") : t("common.assign", "Assign")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
